import { FC, useState, useEffect, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { DeliveryOption } from 'shared/types/cart';
import Button from 'components/commercetools-ui/atoms/button';
import Loading from 'components/commercetools-ui/atoms/loading';
import Modal from 'components/commercetools-ui/atoms/modal';
import Radio from 'components/commercetools-ui/atoms/radio';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';
import useProcessing from 'helpers/hooks/useProcessing';
import { useCart } from 'frontastic';

type ModalProps = {
  modalIsOpen: boolean;
  closeModal: () => void;
  onSelectDeliveryOption: (deliveryOptionId: string) => void;
};

const AmazonPayDeliveryModeModal: FC<ModalProps> = ({ modalIsOpen, closeModal, onSelectDeliveryOption }) => {
  const { formatMessage: formatCheckoutMessage } = useFormat({ name: 'checkout' });
  const { getAvailableDeliveryOptions } = useCart();
  const { getCountry } = useLocale();
  const [deliveryOptions, setDeliveryOptions] = useState<DeliveryOption[] | null>(null);
  const countryCode = getCountry();
  const [loadingData, setLoadingData] = useState(false);
  const [selectedId, setSelectedId] = useState('HomeDelivery');
  const memoizedGetAvailableDeliveryOptions = useCallback(getAvailableDeliveryOptions, []);
  const { processing, startProcessing } = useProcessing();

  const chooseDeliveryOption = () => {
    onSelectDeliveryOption(selectedId);
    startProcessing();
  };

  interface DeliveryOptionsRef {
    [key: string]: string;
  }

  const deliveryOptionsRef: DeliveryOptionsRef = {
    HomeDelivery: formatCheckoutMessage({ id: 'home.delivery', defaultMessage: 'Home Delivery' }),
    StoreCollection: formatCheckoutMessage({ id: 'store.collection', defaultMessage: 'Store Collection' }),
  };

  useEffect(() => {
    const fetchDeliveryOptions = async () => {
      try {
        setLoadingData(true);
        const deliveryOptions = await memoizedGetAvailableDeliveryOptions(countryCode);
        setDeliveryOptions(deliveryOptions);
        setLoadingData(false);
      } catch (error) {
        console.error(error);
      }
    };
    fetchDeliveryOptions();
  }, []);

  return (
    <Modal
      shouldCloseOnOverlayClick
      isOpen={modalIsOpen}
      style={{ content: { overflow: 'hidden', background: 'white' } }}
      className="w-[100%] bg-white md:w-[400px]"
      onRequestClose={closeModal}
    >
      <>
        <XMarkIcon
          className="absolute right-15 top-15 h-24 w-24 hover:cursor-pointer"
          strokeWidth={1}
          color="#494949"
          onClick={closeModal}
        />

        <h3 className="absolute left-36 top-15 text-20">
          {formatCheckoutMessage({ id: 'choose.delivery.option', defaultMessage: 'Choose the delivery option' })}
        </h3>

        <div className="p-36">
          <div className="mt-24 bg-white md:max-w-[800px]">
            {loadingData && (
              <div className="col-span-3">
                <Loading />
              </div>
            )}
            {deliveryOptions &&
              deliveryOptions.map((deliveryOption) => (
                <div
                  key={deliveryOption.deliveryOptionId}
                  className="p-16 pl-0"
                  onClick={() => setSelectedId(deliveryOption.deliveryOptionId)}
                >
                  <div className="">
                    <div className="inline-block h-21">
                      <Radio name="checkout-shipping-method" checked={deliveryOption.deliveryOptionId === selectedId} />
                    </div>
                    <div className="inline-block h-21 pl-16">
                      <p className="relative bottom-1 text-16 font-medium">
                        {deliveryOptionsRef[deliveryOption.deliveryOptionId]}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
          </div>

          {!loadingData && (
            <div className="mt-24">
              <Button
                variant="primary"
                className="w-full min-w-[200px] md:text-14 lg:w-fit lg:px-36"
                loading={processing}
                type="button"
                onClick={chooseDeliveryOption}
              >
                {formatCheckoutMessage({ id: 'continue', defaultMessage: 'Continue' })}
              </Button>
            </div>
          )}
        </div>
      </>
    </Modal>
  );
};

export default AmazonPayDeliveryModeModal;
